import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID } from "../../api/query";
import { CREATE_DEPENDENCY } from "../../api/mutation";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import InputsNew from "../../components/common/InputsNew";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";

interface AddDependencyConfig {
    memberName: string
    memberID: string
    dependencyRsn: string
}

const addDependencySchema = yup.object().shape({
    memberName: yup.string().required('This field is required'),
    memberID: yup.string().required('This field is required'),
    dependencyRsn: yup.string()
        .test('not-only-whitespace', 'Please add a valid Reason', value => !value || value.trim().length > 0)
        .required('This field is required')
});


const AddDependency = () => {
    const navigate = useNavigate()

    const location = useLocation();
    const state = location.state as {
        taskID?: string | null
    };

    const { taskID } = state

    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';
    const employeeName = `${userInfo?.first_name} ${userInfo?.last_name}`

    const [dependentPersonOptions, setDependentPersonOptions] = useState<{ key: string; name: string }[]>([]);

    // react hook form
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control,
        getValues
    } = useForm<any>({
        resolver: yupResolver(addDependencySchema)
    });


    //api
    const [
        getEmployeesByAgencyID,
        { data: employeesData, loading: employeesDataLoading },
    ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
        fetchPolicy: 'no-cache'
    });

    const [createDependency, { loading: createDependencyLoading }] = useMutation(CREATE_DEPENDENCY, {
        fetchPolicy: 'no-cache',
    })

    useEffect(() => {
        if (organizationID) {
            getEmployeesByAgencyID({
                variables: {
                    agency_id: parseInt(organizationID)
                }
            });
        }
    }, [organizationID])

    useEffect(() => {
        if (employeesData && employeesData.employees_by_agency_id) {
            const employeeOptions = employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
                key: emp.employee_id,
                name: `${emp.first_name} ${emp.last_name}`
            }));
            setDependentPersonOptions(employeeOptions);
        }
    }, [employeesData])

    //watch
    const watchMemberName = watch('memberName')

    function handleSelectMember(key: number, name: string): void {
        setValue('memberID', key)
        setValue('memberName', name)
        trigger('memberName')
    }

    async function onSubmit(data: AddDependencyConfig) {
        const input = {
            task_id: taskID ? parseInt(taskID) : null,
            dependent_person_id: parseInt(data.memberID),
            dependent_person_name: data.memberName,
            blocking_person_id: parseInt(employeeID),
            blocking_person_name: employeeName,
            dependency_reason: data.dependencyRsn,
            is_resolved: false,
            created_by: parseInt(employeeID)
        }

        try {
            const response = await createDependency({ variables: { input: input } });
            if (response.data.create_dependency.status_code === 201) {
                showSuccess(response.data.create_dependency.message)
                navigate(-1)
            }
            if (response.data.create_dependency.status_code === 400) {
                showError(response.data.create_dependency.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} id="cts-respon-p-dv" className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    {/* <div className="self-stretch rounded flex flex-col items-start justify-start text-dimgray">
                                <div className="flex flex-row items-center justify-start">
                                    <div className="overflow-hidden flex flex-row items-center justify-start gap-2">
                                        <div className="relative [text-decoration:underline]">Dashboard</div>
                                        <img className="w-4 relative h-4 overflow-hidden shrink-0" alt="" src="Right 16px.svg" />
                                        <div className="relative font-semibold text-gray">Add New Task</div>
                                    </div>
                                </div>
                            </div> */}
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Add Dependency</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        {/* <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5> */}
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <SearchableInputsDropdown
                                label="Dependent Person*"
                                initialValue={watchMemberName}
                                placeHolder="Select Person"
                                options={dependentPersonOptions}
                                onSelect={handleSelectMember}
                                errorMessage={errors?.memberName}
                            />
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Dependency Reason"
                                    label="Dependency Reason*"
                                    type="textarea"
                                    register={register('dependencyRsn')}
                                    errorMessage={errors.dependencyRsn}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                // onClick={handleGoBack}
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </form >
            {(employeesDataLoading || createDependencyLoading) && (
                <Loader />
            )}
        </>
    )
}

export default AddDependency